import { bool, node, string, func, oneOfType, object } from 'prop-types'
import { Component, Fragment } from 'react'
import { render, findDOMNode } from 'react-dom'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import * as Higgs from '@opallabs/higgs'
import { warn } from '@opallabs/quanta'
import { kuiperClassNames, registerHotKey, unregisterHotKey } from '../utils'

import './styles.scss'

/* ------- EXPLANATION OF CONTENTS -------

  This file contains two classical components that leverage the
  react component lifecycle and other non-react, DOM functions
  to render slide menus and their content. At time of writing
  there are two drawers. They hold content for
  `PersonalPanel` and `ViewSetttings` in AssingmentsView.

  COMPONENTS:

  ChestOfDrawers  - In charge of mounting the Chest system in the DOM
                    and unmounting and removing hotkeys when on unmount

  Drawer          - This classical component does not return its own JSX,
                    but instead ensures that the Chest is rendered in window,
                    passes JSX for drawer to global set `drawers`, and manages
                    updating and unregistering the drawer object in `drawers`
                    with lifecycle methods

  ADDITIONAL FUNCTIONS/VARIABLES:

  global variables        - Just below, these non-function varaibles allow access
                            to the chest of drawers in window scope.
                            Thinking of this as a detached redux state is maybe
                            the most pragmatic perspective to take

  currentDrawerContent    - this is called in the render of ChestOfDrawers and
                            gets the NODE saved in drawers[currentDrawerName]
                            and renders it in div.chest-of-drawers

  open/update/closeDrawer - sets classes on the div.chest-of-drawers to set correct
                            context for render of new JSX

  updateChests            - The real engine in the file... called in
                            open/update/closeDrawer, this function
                            force updates all chests in collection,
                            subsequently rerunnning currentDrawerContent
                            in any registered Chest, and updating the Drawer
                            contents

  ------- EXPLANATION OF CONTENTS ------- */

const drawers = {}
const chests = new Set()
const hotKeyMap = {}
let currentDrawerName
let chestInstance

export { drawers, chests }

const trackChest = (chest) => chests.add(chest)
const removeChest = (chest) => chests.remove(chest)

const updateChest = (chest) => chest && chest.forceUpdate()
const updateChests = () => chests.forEach(updateChest)

export const openDrawer = (name, toggleType) => {
  const withoutOverlay = drawers[name].withoutOverlay

  /* if a previous drawer was open
  AND we are opening another drawer without closing the previous
  AND the previous drawer has an onClose callback, fire it here. */
  if (
    !!currentDrawerName &&
    currentDrawerName !== name &&
    !!drawers[currentDrawerName].onClose
  ) {
    drawers[currentDrawerName].onClose()
  }

  if (!withoutOverlay && typeof window.hideSelector !== 'undefined') {
    window.hideSelector('#body_wrapper')
    window.hideSelector('#base_wrapper')
  }

  currentDrawerName = name
  chestInstance.className = kuiperClassNames('chest-of-drawers', {
    /* current drawer name is set and unset on open/update/closeDrawer */
    open: currentDrawerName,
    /* withoutOverlay refers to the Chest's parent div,
      which may or may not cover the entirety of the screen
      boolean _without_ was chosen because the original
      implementation was set to cover the whole screen by default.
      This prop was added subsequently */
    'without-overlay': withoutOverlay,
  })
  if (drawers[name].onOpen) {
    drawers[name].onOpen(toggleType)
  }
  updateChests()
}

export const closeDrawer = (name, toggleType) => {
  if (!name) return
  const withoutOverlay = drawers[name] ? drawers[name].withoutOverlay : false

  if (currentDrawerName === name) {
    currentDrawerName = undefined
  }

  if (drawers[name] && drawers[name].onClose) {
    drawers[name].onClose(toggleType)
  }

  if (
    !withoutOverlay &&
    typeof window.showSelector !== 'undefined' &&
    !currentDrawerName
  ) {
    window.showSelector('#base_wrapper')
    window.showSelector('#body_wrapper')
  }

  if (chestInstance) {
    chestInstance.className = kuiperClassNames('chest-of-drawers', {
      open: currentDrawerName,
    })
  }

  updateChests()
}

export const closeCurrentDrawer = () => {
  setTimeout(() => {
    closeDrawer(currentDrawerName)
  }, 150)
}

export const toggleDrawer = (name, toggleType) => {
  if (currentDrawerName === name) {
    return closeDrawer(name, toggleType)
  }
  return openDrawer(name, toggleType)
}

const unregisterDrawer = ({ name, hotKey }) => {
  if (hotKey) {
    unregisterHotKey(hotKey)
  }
  delete drawers[name]
}

export const registerDrawer = ({
  name,
  hotKey,
  content,
  onOpen,
  onClose,
  withoutOverlay,
}) => {
  if (drawers[name]) {
    warn(
      `A drawer named '${name}' is already loaded. Ignoring the new drawer being loaded.`
    )
    return
  }
  if (hotKeyMap[hotKey]) {
    warn(
      `A drawer with the hot key '${hotKey}' is already loaded. Ignoring the hot key for the new drawer being loaded.`
    )
    return
  }
  const handler = toggleDrawer.bind(this, name, 'keyboard_shortcut')
  drawers[name] = {
    content,
    handler,
    name,
    onOpen,
    onClose,
    withoutOverlay,
  }
  if (typeof content === 'function') {
    setTimeout(() => (drawers[name].content = content()))
  }
  if (hotKey) {
    registerHotKey(hotKey, handler)
  }
}

export const updateDrawer = ({ name, content, ...others }) => {
  drawers[name] = {
    ...drawers[name],
    content,
    ...others,
  }
  drawers[name].content = content
  if (typeof content === 'function') {
    setTimeout(() => (drawers[name].content = content()))
  }
  updateChests()
}

const CurrentDrawerContent = (props) => {
  if (currentDrawerName && drawers[currentDrawerName]) {
    const drawer = drawers[currentDrawerName]

    let content = drawer.content
    if (typeof content === 'function') {
      content = content()
    }

    return (
      <CSSTransition
        key={currentDrawerName}
        classNames="kuiper-slideout"
        timeout={200}
        {...props}
      >
        <div
          id={drawer.htmlId}
          className={kuiperClassNames(`drawer-${currentDrawerName}`, {
            'current-drawer': !drawer.disableKuiperStyles,
          })}
          css={drawers[currentDrawerName].styleOverrides}
        >
          {content}
        </div>
      </CSSTransition>
    )
  }
  return <div />
}

const renderLeftHandPreview = () => {
  if (currentDrawerName && drawers[currentDrawerName]) {
    const leftHandPreview = drawers[currentDrawerName].leftHandPreview
    if (typeof leftHandPreview === 'function') {
      return leftHandPreview()
    }
    return leftHandPreview
  }
  return null
}

export class ChestOfDrawers extends Component {
  componentDidMount() {
    trackChest(this)
    registerHotKey('esc', closeCurrentDrawer)
  }

  componentWillUnmount() {
    removeChest(this)
    unregisterHotKey('esc', closeCurrentDrawer)
  }

  handleOutsideClick(e) {
    // eslint-disable-next-line react/no-find-dom-node
    const domElement = findDOMNode(this)
    if (domElement && domElement !== e.target && !domElement.contains(e.target))
      closeDrawer(currentDrawerName)
  }

  render() {
    return (
      <Fragment>
        <TransitionGroup>
          <CurrentDrawerContent />
        </TransitionGroup>
        {renderLeftHandPreview()}
      </Fragment>
    )
  }
}

const ensureChestExists = () => {
  if (chestInstance) {
    return
  }
  chestInstance = document.createElement('div')
  chestInstance.className = kuiperClassNames('chest-of-drawers', {
    open: currentDrawerName,
  })

  document.body.appendChild(chestInstance)
  render(
    <Higgs.Provider>
      <ChestOfDrawers />
    </Higgs.Provider>,
    chestInstance
  )
}

const shouldUpdateDrawer = (prevProps, props) =>
  prevProps.children !== props.children ||
  prevProps.leftHandPreview !== props.leftHandPreview ||
  prevProps.onClose !== props.onClose ||
  prevProps.withoutOverlay !== props.withoutOverlay

export default class Drawer extends Component {
  static propTypes = {
    children: node.isRequired,
    disableKuiperStyles: bool,
    hotKey: string,
    htmlId: string,
    leftHandPreview: oneOfType([func, string, node]),
    name: string.isRequired,
    onClose: func,
    onOpen: func,
    styleOverrides: object,
    withoutOverlay: bool,
  }

  static defaultProps = {
    disableKuiperStyles: false,
    styleOverrides: {},
  }

  componentDidMount() {
    ensureChestExists()
    const { children, ...otherProps } = this.props
    registerDrawer({
      content: children,
      ...otherProps,
    })
  }

  componentDidUpdate(prevProps) {
    if (!currentDrawerName) return

    if (shouldUpdateDrawer(prevProps, this.props)) {
      const { children, ...otherProps } = this.props

      updateDrawer({
        content: children,
        ...otherProps,
      })
    }
  }

  componentWillUnmount() {
    const { name, hotKey } = this.props
    unregisterDrawer({ name, hotKey })
  }

  render() {
    return null
  }
}
